import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import Box from '@mui/material/Box';
import { IResponsiveList } from './model';
import ListHeader from './components/ListHeader';
import ListContent from './components/ListContent';
import ListSideComponent from './components/ListSideComponent';
import { Collapse, Paper } from '@mui/material';
import Loader from './components/Loader';

const ResponsiveListContainer: React.FC<IResponsiveList> = ({
    mainContent,
    TitleComponent,
    sideComponentData = [],
    topChips,
    lowerChips,
    handleChipClick,
                                                                handleTopChipClick,
    dropdownFilter,
    SideComponent,
    LeftSideStickyComponent,
    SingleRowComponent,
    customActionComponent,
    setDisplayCustomerActions,
    topChipsActions,
    FooterComponent,
    HeaderSideComponent,
    singleRowGap,
    setData,
    SingleRowOnClick,
    setOpenDrawer,
                                                                outsideHooks,
    displayTopActionsComponent,
    displaySearch = true,
    overflowState = true
}) => {
    const [displayedData, setDisplayedData] = useState(mainContent);
    const [showChips, setShowChips] = useState(true);
    const [showFooter, setShowFooter] = useState(true);
    const [loading, setLoading] = useState(true);

    const containerRef = useRef<HTMLDivElement>(null);
    const dataRef = useRef(mainContent);
    const dataLengthRef = useRef(0);
    const memoizedMainContent = useMemo(() => mainContent, [mainContent]);

    useEffect(() => {
        if (displayedData.length === 0) {
            setDisplayedData(memoizedMainContent);
        }

        setDisplayedData(memoizedMainContent);
        console.log('mainContent', memoizedMainContent);
        dataLengthRef.current = memoizedMainContent.length;

        if (dropdownFilter) {
            dropdownFilter.setup(memoizedMainContent, setDisplayedData);
        }

        if (topChips) {
            setShowChips(true);
        }
        setLoading(false);
        dataRef.current= memoizedMainContent;
    }, [memoizedMainContent]);

    const handleScroll = useCallback(() => {
        requestAnimationFrame(() => {
            if (containerRef.current) {
                const scrollTop = containerRef.current.scrollTop;
                const scrollHeight = containerRef.current.scrollHeight;
                const clientHeight = containerRef.current.clientHeight;

                // Determine if the user is at the bottom
                const isAtBottom = scrollTop + clientHeight >= scrollHeight - 5; // 5px buffer
                const isAtTop = scrollTop < 10; // Check if at the top

                setShowChips(isAtTop);
                setShowFooter(!isAtBottom); // Hide footer when at the bottom
            }
        });
    }, []);

    const setContainerRef = useCallback(
        (node: HTMLDivElement | null) => {
            if (node) {
                containerRef.current = node;
                node.addEventListener('scroll', handleScroll); // Add listeners here if needed
            } else if (containerRef.current) {
                // Clean up the previous ref
                containerRef.current.removeEventListener('scroll', handleScroll);
                containerRef.current = null;
            }
        },
        [handleScroll]
    );

    useEffect(() => {
        if (displayedData.length !== mainContent.length) {
            dataLengthRef.current = displayedData.length;
        }
    }, [displayedData]);

    return (
        <>
            {/* Main content */}
            {!loading && (
                <>
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            width: '100vw',
                            height: '100vh',
                            maxHeight: '100dvh',
                            minHeight: 0,
                            overflow: 'hidden',
                            boxSizing: 'border-box',
                            backgroundColor: 'white',
                            padding: '0px',
                            '&::-webkit-scrollbar': {
                                display: 'none'
                            }
                        }}
                    >
                        {customActionComponent ? customActionComponent : null}
                        <ListHeader
                            TopChipsActionComponent={topChipsActions ? topChipsActions : null}
                            TopChipsComponent={topChips ? topChips : null}
                            LowerChipsComponent={lowerChips}
                            setDisplayedData={setDisplayedData}
                            handleChipClick={handleChipClick}
                            handleTopChipClick={handleTopChipClick}
                            showChips={showChips}
                            dropdownOptions={dropdownFilter}
                            dataRef={dataRef}
                            counterRef={dataLengthRef}
                            mainContent={mainContent}
                            HeaderSideComponent={HeaderSideComponent ? HeaderSideComponent : null}
                            TitleComponent={TitleComponent}
                            outsideHooks={outsideHooks}
                            displayTopActionsComponent={displayTopActionsComponent ? displayTopActionsComponent : false}
                            displaySearch={displaySearch}
                        />
                        {/* Main Content Layout */}
                        <Paper
                            sx={{
                                display: 'flex',
                                flexDirection: 'row',
                                flex: 1,
                                minHeight: 0,
                                width: '100%',
                                height: 'auto',
                                overflow: 'hidden',
                                backgroundColor: 'grey',
                                boxShadow: 1,
                                borderRadius: '15px'
                            }}
                        >
                            {/* ListContent */}
                            <Box
                                sx={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    flex: '1 1 70%',
                                    maxWidth: LeftSideStickyComponent ? '80%' : '100%',
                                    width: 'auto',
                                    minHeight: 0,
                                    boxSizing: 'border-box',
                                    height: '100%'
                                }}
                            >
                                <ListContent
                                    data={displayedData}
                                    ref={setContainerRef}
                                    Component={SingleRowComponent}
                                    setData={setData}
                                    setDisplayCustomerActions={setDisplayCustomerActions}
                                    singleRowGap={singleRowGap}
                                    SingleRowOnClick={SingleRowOnClick}
                                    setOpenDrawer={setOpenDrawer}
                                    outsideHooks={outsideHooks}
                                    overflowState={overflowState}
                                />
                            </Box>

                            {/* Side Menu */}
                            <Box
                                sx={{
                                    flex: '1 1 20%',
                                    maxWidth: '20%',
                                    boxSizing: 'border-box',
                                    height: '100%',
                                    backgroundColor: 'inherit',
                                    textAlign: 'center',
                                    display: LeftSideStickyComponent ? 'flex' : 'none',
                                    flexDirection: 'column'
                                }}
                            >
                                {/* LeftSideSticky */}
                                {LeftSideStickyComponent && (
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            flexDirection: 'column',
                                            justifyContent: 'center',
                                            alignItems: 'center',
                                            width: '100%',
                                            height: '25vh'
                                        }}
                                    >
                                        <LeftSideStickyComponent dataLength={displayedData.length} />
                                    </Box>
                                )}

                                <Box
                                    sx={{
                                        height: '100%',
                                        overflowY: 'scroll', // Enable scrolling
                                        '&::-webkit-scrollbar': {
                                            display: 'none' // Chrome, Safari, modern Edge
                                        }
                                    }}
                                >
                                    {SideComponent && (
                                        <ListSideComponent>
                                            {loading ? (
                                                <Loader />
                                            ) : (
                                                <SideComponent
                                                    displayData={sideComponentData}
                                                    fullData={mainContent}
                                                    setFullData={setDisplayedData}
                                                />
                                            )}
                                        </ListSideComponent>
                                    )}
                                </Box>
                            </Box>
                        </Paper>
                    </Box>
                    {FooterComponent && (
                        <Collapse in={showFooter} timeout={200}>
                            <Box
                                sx={{
                                    width: '100%',
                                    display: 'flex',
                                    position: 'fixed',
                                    bottom: 0,
                                    backgroundColor: 'white',
                                    boxShadow: 1,
                                    textAlign: 'center'
                                }}
                            >
                                <FooterComponent />
                            </Box>
                        </Collapse>
                    )}
                </>
            )}
        </>
    );
};

export default ResponsiveListContainer;
